import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Button from '../components/Button';

const Registration = () => {
    const navigate = useNavigate();
    const location = useLocation();

    // Initialize form with state from navigation if available, or defaults
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        phone: '',
        age: '',
        plan: 'monthly',
        course: '',
        programCategory: 'regular',
        experience: 'beginner',
        batch: 'evening',
        address: ''
    });

    useEffect(() => {
        if (location.state?.programCategory) {
            setFormData(prev => ({
                ...prev,
                programCategory: location.state.programCategory
            }));
        }
    }, [location.state]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value,
            // Reset course when category changes to prevent invalid selection
            ...(name === 'programCategory' && { course: '' })
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        // Basic validation
        if (!formData.name || !formData.email || !formData.phone || !formData.course) {
            alert('Please fill in all required fields');
            return;
        }
        try {
            // Save registration to backend so the server has data to include in the CSV/email
            const res = await fetch('/api/register.php', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(formData)
            });

            const result = await res.json();
            if (!res.ok) {
                throw new Error(result.error || 'Failed to save registration');
            }

            // Proceed to payment on successful registration
            navigate('/payment', { state: { formData } });

            // Reset form
            setFormData({
                name: '',
                email: '',
                phone: '',
                age: '',
                plan: 'monthly',
                course: '',
                programCategory: 'regular',
                experience: 'beginner',
                batch: 'evening',
                address: ''
            });
        } catch (error) {
            console.error('Registration submit error:', error);
            alert('Failed to complete registration. Please try again or contact support.');
        }
    };

    return (
        <>
            {/* Hero Section */}
            <section className="hero" style={{ minHeight: '60vh' }}>
                <div className="hero-content">
                    <h1 className="hero-title">Join Silambam Academy</h1>
                    <p className="hero-subtitle">Begin Your Warrior Journey Today</p>
                </div>
            </section>

            {/* Registration Form Section */}
            <section className="section section-dark">
                <div className="section-header">
                    <h2 className="section-title">Registration Form</h2>
                    <p style={{ textAlign: 'center', fontSize: '1.2rem', color: 'var(--color-gray-light)', maxWidth: '800px', margin: '1rem auto' }}>
                        Fill out the form below to register. You'll receive payment instructions via email.
                    </p>
                </div>

                <div style={{ maxWidth: '800px', margin: '0 auto' }}>
                    <form onSubmit={handleSubmit}>
                        <div className="form-group">
                            <label className="form-label" htmlFor="name">Full Name *</label>
                            <input
                                type="text"
                                id="name"
                                name="name"
                                value={formData.name}
                                onChange={handleChange}
                                className="form-input"
                                required
                                placeholder="Enter your full name"
                            />
                        </div>

                        <div className="form-group">
                            <label className="form-label" htmlFor="email">Email Address *</label>
                            <input
                                type="email"
                                id="email"
                                name="email"
                                value={formData.email}
                                onChange={handleChange}
                                className="form-input"
                                required
                                placeholder="your.email@example.com"
                            />
                        </div>

                        <div className="form-group">
                            <label className="form-label" htmlFor="phone">Phone Number *</label>
                            <input
                                type="tel"
                                id="phone"
                                name="phone"
                                value={formData.phone}
                                onChange={handleChange}
                                className="form-input"
                                required
                                placeholder="+91 12345 67890"
                            />
                        </div>

                        <div className="form-group">
                            <label className="form-label" htmlFor="age">Age</label>
                            <input
                                type="number"
                                id="age"
                                name="age"
                                value={formData.age}
                                onChange={handleChange}
                                className="form-input"
                                placeholder="Your age"
                                min="5"
                                max="100"
                            />
                        </div>

                        {/* <div className="form-group">
                            <label className="form-label" htmlFor="plan">Select Plan *</label>
                            <select
                                id="plan"
                                name="plan"
                                value={formData.plan}
                                onChange={handleChange}
                                className="form-select"
                                required
                            >
                                <option value="monthly">Monthly Plan - ₹999</option>
                                <option value="quarterly">Quarterly Plan - ₹2,499 (Most Popular)</option>
                                <option value="pro">Pro Warrior Plan - ₹4,999</option>
                            </select>
                        </div> */}

                        <div className="form-group">
                            <label className="form-label" htmlFor="programCategory">Program Category *</label>
                            <select
                                id="programCategory"
                                name="programCategory"
                                value={formData.programCategory}
                                onChange={handleChange}
                                className="form-select"
                                required
                            >
                                <option value="regular">Regular Program</option>
                                <option value="shortTerm">Short-Term Program</option>
                            </select>
                        </div>

                        <div className="form-group">
                            <label className="form-label" htmlFor="course">Select Plan/Course *</label>
                            <select
                                id="course"
                                name="course"
                                value={formData.course}
                                onChange={handleChange}
                                className="form-select"
                                required
                            >
                                <option value="">Choose an option...</option>
                                {formData.programCategory === 'regular' ? (
                                    <>
                                        <option value="monthly">Monthly - ₹999 / $50 USD</option>
                                        <option value="semi_annual">Semi-annual - ₹4,499 / $250 USD</option>
                                        <option value="annual">Annual - ₹9,999 / $550 USD</option>
                                    </>
                                ) : (
                                    <>
                                        <option value="introduction">Introduction - Free / Free</option>
                                        <option value="beginner_course">Beginner course - ₹499</option>
                                        <option value="15_days">15 days - ₹599 / $25 USD</option>
                                        <option value="30_days">30 days - ₹999 / $50 USD</option>
                                    </>
                                )}
                            </select>
                        </div>

                        <div className="form-group">
                            <label className="form-label" htmlFor="experience">Martial Arts Experience</label>
                            <select
                                id="experience"
                                name="experience"
                                value={formData.experience}
                                onChange={handleChange}
                                className="form-select"
                            >
                                <option value="beginner">Complete Beginner</option>
                                <option value="some">Some Experience (Other Martial Arts)</option>
                                <option value="intermediate">Intermediate (Silambam or Similar)</option>
                                <option value="advanced">Advanced (3+ years)</option>
                            </select>
                        </div>

                        <div className="form-group">
                            <label className="form-label" htmlFor="batch">Preferred Batch Timing *</label>
                            <select
                                id="batch"
                                name="batch"
                                value={formData.batch}
                                onChange={handleChange}
                                className="form-select"
                                required
                            >
                                <option value="morning">Morning (6:00 AM - 7:00 AM IST)</option>
                                <option value="evening">Evening (6:00 PM - 7:00 PM IST)</option>
                                <option value="night">Night (9:00 PM - 10:00 PM IST)</option>
                            </select>
                        </div>

                        <div className="form-group">
                            <label className="form-label" htmlFor="address">Address</label>
                            <textarea
                                id="address"
                                name="address"
                                value={formData.address}
                                onChange={handleChange}
                                className="form-textarea"
                                placeholder="Your city and country"
                                rows="3"
                            ></textarea>
                        </div>

                        <Button type="submit" style={{ width: '100%', fontSize: '1.2rem', padding: '1.2rem' }}>
                            Complete Registration
                        </Button>

                        <p style={{ textAlign: 'center', marginTop: '2rem', color: 'var(--color-gray-light)' }}>
                            After submitting, you'll receive payment instructions via email. Classes begin upon payment confirmation.
                        </p>
                    </form>
                </div>
            </section>
        </>
    );
};

export default Registration;
